import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  Alert, CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const empty = {
  processDate: new Date().toISOString().split('T')[0],
  grainLotId: '',
  grainLotNumber: '',
  inputQtyMt: '',
  degermedEndospermQty: '',
  germQty: '',
  brokenGritsQty: '',
  dustLossQty: '',
  conditioningMoisture: '',
  rollerGapMm: '',
  aspiratorSpeedRpm: '',
  degerminatorType: '',
  operatorName: '',
  notes: '',
};

const numKeys = [
  'inputQtyMt','degermedEndospermQty','germQty','brokenGritsQty','dustLossQty',
  'conditioningMoisture','rollerGapMm','aspiratorSpeedRpm',
];

const NF = ({ label, k, form, set, helper }: any) => (
  <Grid item xs={12} sm={6} md={4}>
    <TextField label={label} type="number" fullWidth size="small" value={form[k]}
      onChange={(e) => set((f: any) => ({ ...f, [k]: e.target.value }))}
      inputProps={{ step: 0.01, min: 0 }} helperText={helper} />
  </Grid>
);

const DegermingForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<any>(empty);

  useEffect(() => {
    if (!isEdit) return;
    millingApi.getDegermingBatch(id!).then((r) => {
      const f: any = { ...empty };
      Object.keys(empty).forEach((k) => { f[k] = r[k] != null ? String(r[k]) : ''; });
      setForm(f);
    }).catch(() => setError('Failed to load batch')).finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload: any = { ...form };
    numKeys.forEach((k) => { payload[k] = payload[k] !== '' ? Number(payload[k]) : undefined; });
    if (!payload.grainLotId) delete payload.grainLotId;
    try {
      if (isEdit) await millingApi.updateDegermingBatch(id!, payload);
      else await millingApi.createDegermingBatch(payload);
      navigate('/milling/degerming');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button onClick={() => navigate('/milling/degerming')}>← Back</Button>
        <Typography variant="h5">{isEdit ? 'Edit Degerming Batch' : 'New Degerming Batch'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Batch Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Process Date *" type="date" fullWidth size="small" value={form.processDate}
                  onChange={(e) => setForm((f: any) => ({ ...f, processDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Grain Lot Number" fullWidth size="small" value={form.grainLotNumber}
                  onChange={(e) => setForm((f: any) => ({ ...f, grainLotNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Degerminator Type" fullWidth size="small" value={form.degerminatorType}
                  onChange={(e) => setForm((f: any) => ({ ...f, degerminatorType: e.target.value }))}
                  helperText="e.g. Satake, Bühler" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Operator" fullWidth size="small" value={form.operatorName}
                  onChange={(e) => setForm((f: any) => ({ ...f, operatorName: e.target.value }))} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Input & Process Parameters</Typography>
            <Grid container spacing={2}>
              <NF label="Input Qty (MT)" k="inputQtyMt" form={form} set={setForm} />
              <NF label="Conditioning Moisture (%)" k="conditioningMoisture" form={form} set={setForm} />
              <NF label="Roller Gap (mm)" k="rollerGapMm" form={form} set={setForm} />
              <NF label="Aspirator Speed (rpm)" k="aspiratorSpeedRpm" form={form} set={setForm} helper="Optional" />
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Output Quantities</Typography>
            <Grid container spacing={2}>
              <NF label="Degermed Endosperm (MT)" k="degermedEndospermQty" form={form} set={setForm} />
              <NF label="Germ (MT)" k="germQty" form={form} set={setForm} />
              <NF label="Broken Grits (MT)" k="brokenGritsQty" form={form} set={setForm} />
              <NF label="Dust Loss (MT)" k="dustLossQty" form={form} set={setForm} />
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextField label="Notes" fullWidth multiline rows={2} value={form.notes}
              onChange={(e) => setForm((f: any) => ({ ...f, notes: e.target.value }))} />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Batch' : 'Save Batch'}
          </Button>
          <Button onClick={() => navigate('/milling/degerming')}>Cancel</Button>
        </Box>
      </form>
    </Box>
  );
};

export default DegermingForm;
